/**
 * =============================================================================
 * IDS CRM Web - Allocate engineers from the Jobs & Tickets board
 * =============================================================================
 *
 * Gareth, 20 October 2026: team leaders, managers and operations
 * coordinators allocate work from the board's details panel, and every
 * allocation confirms the date the work must be completed by - the engineer is
 * told it in the assignment alert (in-app, email, Teams).
 *  - A ticket has one engineer: pick, confirm "Complete by", Assign
 *    (PUT /work-board/ticket/:id/engineer, which keeps the history, moves the
 *    SLA resolution due to that day and tells the engineer).
 *  - Engineer work and maintenance take several engineers: the same picker as
 *    their edit pages, with the booking warning under each name (a warning,
 *    never a block), saved through their own routes so every rule there still
 *    applies (PUT /tasks/:id engineer_ids + scheduled_date;
 *    PUT /maintenance/tasks/:id assigned_engineer_ids + current_due_date).
 *    The first engineer on a job is the lead.
 * The date is filled in with the current one and can be changed; a date that
 * has already passed must be changed before saving.
 *
 * The engineer view (EngineerBoard.js) saves a drag with saveAllocation and
 * shows the same date field and booking warning.
 * =============================================================================
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import EngineerPicker, { moveToFront } from './EngineerPicker';
import { savedEngineerIds, engineerClashSentence } from '../pages/TaskForm';
import { ukTodayKey, formatDateKey } from '../utils/dates';

const CLASH_DEBOUNCE_MS = 300;
const TIME_PATTERN = /^\d{2}:\d{2}/;
const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const clockOf = (value) => (value && TIME_PATTERN.test(String(value)) ? String(value).substring(0, 5) : '');
const dayOf = (value) => (value ? String(value).split('T')[0] : '');
const sameList = (a, b) => a.length === b.length && a.every((value, index) => value === b[index]);

/** Where a job's engineers and date are read and saved, by kind of card. */
const JOB_SOURCES = {
  scheduled_work: {
    read: (id) => `/api/v1/tasks/${id}`,
    save: (id) => `/api/v1/tasks/${id}`,
    field: 'engineer_ids',
    dateField: 'scheduled_date',
    idsFrom: (data) => (data.assigned_group === 'maintenance_planning_owner' && !data.assigned_to_id ? [] : savedEngineerIds(data)),
    when: (data) => ({
      date: dayOf(data.scheduled_date),
      start: data.is_all_day ? '' : clockOf(data.scheduled_start_time),
      duration: Number(data.estimated_duration_mins) || null,
      allDay: Boolean(data.is_all_day)
    }),
    lead: true
  },
  scheduled_maintenance: {
    read: (id) => `/api/v1/maintenance/tasks/${id}`,
    save: (id) => `/api/v1/maintenance/tasks/${id}`,
    field: 'assigned_engineer_ids',
    dateField: 'current_due_date',
    idsFrom: (data) => (Array.isArray(data.assigned_engineer_ids) ? data.assigned_engineer_ids.filter(Boolean) : []),
    when: (data) => ({
      date: dayOf(data.current_due_date),
      start: clockOf(data.scheduled_start_time),
      duration: Number(data.estimated_duration_mins) || null,
      allDay: false
    }),
    lead: false
  }
};

/** The UK day of a ticket's SLA resolution due ('' with none). */
export const ticketDueKey = (card) => (card && card.sla_resolution_due ? ukTodayKey(new Date(card.sla_resolution_due)) : '');

/** Why a completion date cannot be saved, or '' when it can. */
export function completeByProblem(dateKey, today = ukTodayKey()) {
  if (!dateKey || !DATE_KEY_PATTERN.test(dateKey)) return 'Give the date the work must be completed by.';
  if (dateKey < today) return 'That date has passed. Choose today or a later date.';
  return '';
}

/**
 * Save who is on a piece of work and its completion date, through the route
 * that owns that kind of work. engineerIds is the whole list (lead first).
 */
export async function saveAllocation(authFetch, card, { engineerIds, dateKey }) {
  let url;
  let payload;
  if (card.work_type === 'reactive_ticket') {
    url = `/api/v1/work-board/ticket/${card.id}/engineer`;
    payload = { assigned_to_id: engineerIds[0], target_date: dateKey };
  } else {
    const source = JOB_SOURCES[card.work_type];
    if (!source) throw new Error('That kind of work cannot be allocated here.');
    url = source.save(card.id);
    payload = { [source.field]: engineerIds, [source.dateField]: dateKey };
  }
  const response = await authFetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || 'The change could not be saved.');
  return data;
}

/**
 * The booking warning under each engineer for a day (as on the edit pages):
 * returns noteFor(userId). A visit is never a clash with itself.
 */
export function useBookingWarnings(card, when, ids) {
  const { authFetch } = useAuth();
  const [clashes, setClashes] = useState({});
  const idsKey = (ids || []).join(',');
  const date = when && when.date;
  const start = when && when.start;
  const duration = when && when.duration;
  const allDay = Boolean(when && when.allDay);

  useEffect(() => {
    if (!card || card.work_type === 'reactive_ticket' || !date || !DATE_KEY_PATTERN.test(date) || !idsKey) {
      setClashes({});
      return undefined;
    }
    let cancelled = false;
    const params = [`date=${date}`];
    if (start) params.push(`start_time=${start}`);
    if (!allDay && Number.isInteger(duration) && duration >= 1 && duration <= 1440) params.push(`duration_mins=${duration}`);
    params.push(`all_day=${allDay ? 'true' : 'false'}`);
    params.push(`engineer_ids=${idsKey.split(',').map(encodeURIComponent).join(',')}`);
    if (card.work_type === 'scheduled_work') params.push(`exclude_task_id=${encodeURIComponent(card.id)}`);
    const timer = setTimeout(async () => {
      try {
        const response = await authFetch(`/api/v1/tasks/booking-clashes?${params.join('&')}`);
        if (!response.ok) {
          if (!cancelled) setClashes({});
          return;
        }
        const data = await response.json();
        if (!cancelled) setClashes(data && data.engineers && typeof data.engineers === 'object' ? data.engineers : {});
      } catch (err) {
        // A warning that cannot be fetched is simply not shown.
        if (!cancelled) setClashes({});
      }
    }, CLASH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [authFetch, card, date, start, duration, allDay, idsKey]);

  return (userId) => {
    const list = (Array.isArray(clashes[userId]) ? clashes[userId] : [])
      .filter(clash => !(clash.kind === 'maintenance' && card && clash.task_id === card.id));
    if (list.length === 0) return null;
    return (
      <div className="availability-warnings">
        {list.map((clash, index) => (
          <span key={index} className="availability-warning-chip">⚠ {engineerClashSentence(clash, date)}</span>
        ))}
      </div>
    );
  };
}

/** "Complete by": a date field, today or later, with what it was before. */
export function CompleteByField({ id, value, onChange, savedKey, disabled, hint }) {
  const today = ukTodayKey();
  const passed = savedKey && savedKey < today && value === savedKey;
  return (
    <div className="board-assign-date">
      <label htmlFor={id}>Complete by</label>
      <input id={id} type="date" value={value} min={today} onChange={(e) => onChange(e.target.value)} disabled={disabled} required />
      {passed && <p className="board-assign-error">The date on it ({formatDateKey(savedKey)}) has passed. Choose a new one.</p>}
      {hint && !passed && <p className="ops-muted board-assign-hint">{hint}</p>}
    </div>
  );
}

/**
 * The dropdown's people: the engineer list, plus whoever has the work now if
 * they are not on it (an admin, someone no longer active), so the dropdown
 * shows them rather than "Choose an engineer".
 */
export function withCurrent(engineers, currentId, currentName) {
  const list = Array.isArray(engineers) ? engineers : [];
  if (!currentId || list.some(engineer => engineer.user_id === currentId)) return list;
  return [{ user_id: currentId, full_name: `${currentName || 'Someone'} (has it now)` }, ...list];
}

const fetchFailed = (err, fallback) => (err.message === 'Failed to fetch' ? fallback : err.message);

function TicketAssign({ card, engineers, onSaved }) {
  const { authFetch } = useAuth();
  const savedKey = ticketDueKey(card);
  const [choice, setChoice] = useState(card.assigned_to_id || '');
  const [dateKey, setDateKey] = useState(savedKey);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setMessage('');
    setError('');
  }, [card.id]);
  useEffect(() => {
    setChoice(card.assigned_to_id || '');
    setDateKey(savedKey);
  }, [card.id, card.assigned_to_id, savedKey]);

  const changed = choice !== (card.assigned_to_id || '') || dateKey !== savedKey;
  const problem = completeByProblem(dateKey);

  const save = async () => {
    if (!choice || problem || !changed) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const data = await saveAllocation(authFetch, card, { engineerIds: [choice], dateKey });
      setMessage(`Assigned to ${data.assigned_to_name || 'the engineer'}, complete by ${formatDateKey(dateKey)}.`);
      onSaved();
    } catch (err) {
      setError(fetchFailed(err, 'The engineer could not be saved. Check your signal and try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="board-assign">
      <label htmlFor={`board-assign-${card.id}`}>Engineer</label>
      <select id={`board-assign-${card.id}`} className="board-assign-single" value={choice} onChange={(e) => setChoice(e.target.value)} disabled={saving}>
        <option value="">Choose an engineer</option>
        {withCurrent(engineers, card.assigned_to_id, card.assigned_to_name).map(engineer => (
          <option key={engineer.user_id} value={engineer.user_id}>{engineer.full_name}</option>
        ))}
      </select>
      <CompleteByField
        id={`board-assign-date-${card.id}`}
        value={dateKey}
        onChange={setDateKey}
        savedKey={savedKey}
        disabled={saving}
        hint="This moves the ticket's SLA resolution due to that day."
      />
      <div className="board-assign-row">
        <button type="button" className="btn-primary btn-small" onClick={save} disabled={saving || !choice || Boolean(problem) || !changed}>
          {saving ? 'Saving…' : 'Assign'}
        </button>
      </div>
      {message && <p className="board-assign-done">{message}</p>}
      {error && <p className="board-assign-error">{error}</p>}
    </div>
  );
}

function JobAssign({ card, engineers, onSaved }) {
  const { authFetch } = useAuth();
  const source = JOB_SOURCES[card.work_type];
  const [job, setJob] = useState(null);
  const [savedIds, setSavedIds] = useState([]);
  const [ids, setIds] = useState([]);
  const [savedKey, setSavedKey] = useState('');
  const [dateKey, setDateKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authFetch(source.read(card.id));
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'The engineers on this job could not be read.');
      }
      const data = await response.json();
      const current = source.idsFrom(data);
      const day = source.when(data).date;
      setJob(data);
      setSavedIds(current);
      setIds(current);
      setSavedKey(day);
      setDateKey(day);
    } catch (err) {
      setJob(null);
      setError(fetchFailed(err, 'The engineers on this job could not be read. Check your signal.'));
    } finally {
      setLoading(false);
    }
  }, [authFetch, card.id, source]);

  useEffect(() => {
    setMessage('');
    load();
  }, [load]);

  // The warning is for the day being chosen, not the day saved.
  const when = useMemo(() => (job ? { ...source.when(job), date: dateKey } : null), [job, source, dateKey]);
  const noteFor = useBookingWarnings(card, when, ids);

  const changed = !sameList(ids, savedIds) || dateKey !== savedKey;
  const problem = completeByProblem(dateKey);

  const save = async () => {
    if (ids.length === 0 || problem || !changed) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await saveAllocation(authFetch, card, { engineerIds: ids, dateKey });
      setSavedIds(ids);
      setSavedKey(dateKey);
      setMessage(`Saved, complete by ${formatDateKey(dateKey)}.`);
      onSaved();
    } catch (err) {
      setError(fetchFailed(err, 'The engineers could not be saved. Check your signal and try again.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !job) return <p className="ops-muted">Reading the engineers…</p>;
  if (!job) return error ? <p className="board-assign-error">{error}</p> : null;

  // Names for anyone on the job who is no longer on the active list.
  const knownNames = {};
  (Array.isArray(job.engineers) ? job.engineers : []).forEach(entry => {
    if (entry && entry.user_id && entry.full_name) knownNames[entry.user_id] = entry.full_name;
  });
  // A sales job from the pipeline has one engineer (the server refuses more).
  const single = card.work_type === 'scheduled_work' && job.task_type === 'sales_action';

  return (
    <div className="board-assign">
      <label htmlFor={`board-assign-${card.id}`}>{single ? 'Engineer' : 'Engineers'}</label>
      {single ? (
        <>
          <select
            id={`board-assign-${card.id}`}
            className="board-assign-single"
            value={ids[0] || ''}
            onChange={(e) => setIds(e.target.value ? [e.target.value] : [])}
            disabled={saving}
          >
            <option value="">Choose an engineer</option>
            {withCurrent(engineers, savedIds[0], knownNames[savedIds[0]]).map(engineer => (
              <option key={engineer.user_id} value={engineer.user_id}>{engineer.full_name}</option>
            ))}
          </select>
          {ids[0] && noteFor(ids[0])}
        </>
      ) : (
        <EngineerPicker
          id={`board-assign-${card.id}`}
          options={engineers}
          selectedIds={ids}
          onChange={setIds}
          knownNames={knownNames}
          emptyText={card.assigned_to_planning_group ? 'Nobody yet: with the Maintenance Planning Owner.' : 'Nobody yet.'}
          leadLabel={source.lead && ids.length > 1 ? 'Lead' : undefined}
          noteFor={noteFor}
          onMakeLead={source.lead ? (userId) => setIds(moveToFront(ids, userId)) : undefined}
        />
      )}
      <CompleteByField
        id={`board-assign-date-${card.id}`}
        value={dateKey}
        onChange={setDateKey}
        savedKey={savedKey}
        disabled={saving}
        hint={card.work_type === 'scheduled_work' ? 'The day the job is booked for (its time stays the same).' : 'The day the visit is due.'}
      />
      <div className="board-assign-row">
        <button type="button" className="btn-primary btn-small" onClick={save} disabled={saving || ids.length === 0 || Boolean(problem) || !changed}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {changed && !saving && (
          <button type="button" className="ops-link-btn" onClick={() => { setIds(savedIds); setDateKey(savedKey); setError(''); }}>Undo changes</button>
        )}
      </div>
      {ids.length === 0 && <p className="ops-muted">Add an engineer first.</p>}
      {message && !changed && <p className="board-assign-done">{message}</p>}
      {error && <p className="board-assign-error">{error}</p>}
    </div>
  );
}

function BoardAssign({ card, engineers, onSaved }) {
  if (card.work_type === 'reactive_ticket') return <TicketAssign card={card} engineers={engineers} onSaved={onSaved} />;
  if (JOB_SOURCES[card.work_type]) return <JobAssign card={card} engineers={engineers} onSaved={onSaved} />;
  return null;
}

export default BoardAssign;
